/**
 * Owner / HR payroll workspace.
 *
 * Renders the new 7-stage pipeline introduced in Phase 1A.1:
 *
 *   draft ─run─▶ draft ─preview─▶ previewed ─approve─▶ approved ─lock─▶ locked ─send─▶ sent
 *                                           └reject─▶ draft (Owner only)
 *
 * Buttons gate themselves on the current status of the period so the
 * UI never offers an invalid transition.
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useRunPayroll,
  usePreviewPayroll,
  useApprovePayroll,
  useRejectPayroll,
  useLockPayroll,
  useSendSlips,
  useDeliveries,
  useRetryDelivery,
  useDataEntries,
  useUpsertAdjustmentEntry,
  useBulkUpsertAdjustment,
  useBulkSetSalary,
  useSyncPayrollInclusion,
  useDeleteDataEntry,
  usePayrollRunDetail,
  useEmployees,
  useOrgDepartments,
  useOrgPositions,
  useBranches,
  usePayOverrides,
  useSetPayOverride,
  useBulkSetPayOverride,
  useCommissionAdjustments,
  type PayrollPreviewResult,
  type DataEntryRow,
} from '../../hooks';
import { apiFetch } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';
import { Modal } from '../../components/ui/Modal';
import { useConfirm } from '../../components/ui/confirm';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { ComplianceAlertList, type ComplianceItem } from '../../components/ui/ComplianceAlert';
import {
  Play, Eye, CheckCircle2, XCircle, Lock, Send, AlertTriangle, RotateCw,
  MailCheck, MailX, MailWarning, Plus, Minus, Trash2, ChevronRight, Printer, Banknote,
  FileSpreadsheet, Upload,
} from 'lucide-react';
import type { PayrollStatus } from '@payroll/shared';
import { lastPeriods } from '../../lib/thai-date';

interface PayrollRow {
  id: string;
  emp_code: string;
  base_pay: number;
  gross: number;
  deductions_total: number;
  net: number;
  status: PayrollStatus;
  tax_amount: number;
  sso_amount: number;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  bank: string | null;
  bank_account_masked: string | null;
}

function getLast6Periods(): string[] {
  return lastPeriods(6);
}

const STAGE_ORDER: PayrollStatus[] = [
  'draft', 'previewed', 'approved', 'locked', 'sent',
];

const STATUS_LABEL: Record<PayrollStatus, string> = {
  draft: 'ร่าง',
  previewed: 'ตัวอย่าง',
  approved: 'อนุมัติแล้ว',
  locked: 'ล็อกแล้ว',
  pdf_ready: 'PDF พร้อม',
  sending: 'กำลังส่ง',
  partial_sent: 'ส่งบางส่วน',
  sent: 'ส่งแล้ว',
  voided: 'ยกเลิก',
};

// Map every status to how far it has progressed along STAGE_ORDER.
// Non-linear states (pdf_ready/sending/partial_sent) all sit at "locked".
function reachedStageIndex(s: PayrollStatus): number {
  const map: Record<PayrollStatus, number> = {
    draft: 0, previewed: 1, approved: 2,
    locked: 3, pdf_ready: 3, sending: 3, partial_sent: 3,
    sent: 4, voided: -1,
  };
  return map[s];
}

/** Content-only version used by the PayrollCycle hub. */
export function OwnerPayrollContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [preview, setPreview] = useState<PayrollPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);

  const runPayroll = useRunPayroll();
  const previewPayroll = usePreviewPayroll();
  const approvePayroll = useApprovePayroll();
  const rejectPayroll = useRejectPayroll();
  const lockPayroll = useLockPayroll();
  const sendSlips = useSendSlips();
  // Only fetch deliveries when the period has moved past lock (otherwise
  // the rows haven't been inserted yet).
  const showDeliveries = rows.length > 0 &&
    ['locked', 'sending', 'partial_sent', 'sent'].includes(rows[0]!.status);
  const deliveries = useDeliveries(showDeliveries ? period : null);
  const retryDelivery = useRetryDelivery();
  const dataEntries = useDataEntries(period);
  const upsertAdj = useUpsertAdjustmentEntry();
  const bulkAdj = useBulkUpsertAdjustment();
  const bulkSalary = useBulkSetSalary();
  const syncInclusion = useSyncPayrollInclusion();
  const removeEntry = useDeleteDataEntry();
  const payOverrides = usePayOverrides(period);
  const setPayOverride = useSetPayOverride();
  const bulkPayOverride = useBulkSetPayOverride();
  const overrideAmountByEmp = new Map((payOverrides.data ?? []).map((o) => [o.emp_code, o.amount]));
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null; department?: string | null; position?: string | null; branch_id?: string | null }> | undefined };
  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const commAdj = useCommissionAdjustments(period);
  const { data: branches = [] } = useBranches();
  const branchNameById = new Map(branches.map((b) => [b.id, b.name]));
  const empMeta = new Map((employees ?? []).map((e) => [e.emp_code, { dept: e.department ?? '', pos: e.position ?? '', branch: e.branch_id ? branchNameById.get(e.branch_id) ?? '' : '' }]));
  const { data: orgDepts = [] } = useOrgDepartments();
  const { data: orgPositions = [] } = useOrgPositions();
  const nameOf = (code: string) => empName.get(code) ?? code;
  const [warnings, setWarnings] = useState<Array<{ emp_code: string; type: string; label: string }>>([]);
  // per-run override: ติ๊กเพื่อข้ามการลงเวลา/ลา/OT งวดนี้ (จ่ายเต็มตามฐาน).
  // ไม่ติ๊ก = ใช้โหมดที่ตั้งไว้ทั้งบริษัท.
  const [salaryOnlyRun, setSalaryOnlyRun] = useState(false);
  // วันที่มีผลของเงินเดือนที่นำเข้า (ไฟล์เดียว) — ว่าง = วันที่ 1 ของงวด. รีเซ็ตเมื่อเปลี่ยนงวด.
  const [salaryEffDate, setSalaryEffDate] = useState('');

  const role = useAuthStore((s) => s.profile?.role ?? 'OWNER');
  const soloMode = useAuthStore((s) => s.profile?.solo_mode ?? false);
  const isOwner = role === 'OWNER';
  // Print/send the approval request is HR's job; an owner only sees it when
  // they also do HR's prep (solo tenant). The owner's screen is approve + bank file.
  const canPrintRequest = !isOwner || soloMode;
  const confirm = useConfirm();
  const consequence = useConsequence();
  const toast = useToast();
  // Adjustment modal (replaces the old 4× window.prompt flow).
  const [adjFor, setAdjFor] = useState<string | null>(null);
  const [adjDir, setAdjDir] = useState<'add' | 'deduct'>('add');
  const [adjPreset, setAdjPreset] = useState<'bonus' | 'wage_correction' | 'reimbursement'>('bonus');
  const [adjLabel, setAdjLabel] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Per-employee detail drawer (click a table row).
  const [detailFor, setDetailFor] = useState<string | null>(null);
  const [deptFilter, setDeptFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');

  const status: PayrollStatus | null = rows.length ? rows[0]!.status : null;
  // R6 (audit 2ส.ค.69) — งวดที่ปิดแล้วต้องกรองจาก "สาขา/แผนก/ตำแหน่ง ณ วันปิดงวด"
  // (มาจาก emp_snapshot ที่ API ส่งมา) ไม่ใช่ค่าปัจจุบันในทะเบียนพนักงาน
  // ไม่งั้นย้ายสาขาทีเดียว ยอดเงินเดือนรายสาขาของเดือนที่ปิดไปแล้วเปลี่ยนตาม
  const metaOf = (r: (typeof rows)[number]) => {
    const snapped = r as typeof r & { department?: string | null; position?: string | null; branch_name?: string | null };
    if (snapped.branch_name != null || snapped.department != null || snapped.position != null) {
      return { dept: snapped.department ?? '', pos: snapped.position ?? '', branch: snapped.branch_name ?? '' };
    }
    return empMeta.get(r.emp_code) ?? { dept: '', pos: '', branch: '' };
  };
  const filteredRows = rows.filter((r) => {
    const m = metaOf(r);
    if (deptFilter && m.dept !== deptFilter) return false;
    if (positionFilter && m.pos !== positionFilter) return false;
    if (branchFilter && m.branch !== branchFilter) return false;
    return true;
  });
  // stepper ในหน้า: รวบรวมเวลา -> เงินเพิ่ม/หัก -> ตรวจยอด -> ปิดงวด+สลิป (ตามสถานะงวด)
  const payrollClosed = !!status && ['locked', 'pdf_ready', 'sending', 'partial_sent', 'sent'].includes(status);
  const payrollStepIdx = payrollClosed ? 4 : !rows.length ? 0 : status === 'draft' ? 1 : status === 'previewed' ? 2 : 3;

  async function loadRows() {
    setLoading(true);
    try {
      const data = await apiFetch<PayrollRow[]>(`/owner/payroll/run?period=${period}`);
      setRows(data);
      setPreview(null);
    } catch {
      setRows([]);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleRun() {
    const ok = await consequence({
      title: `คำนวณร่างเงินเดือน งวด ${period}`,
      intro: 'ระบบจะคำนวณร่างเงินเดือนจากเวลาเข้างาน เงินเดือน และรายการปรับล่าสุด',
      effects: [
        'เป็นแค่ "ร่าง" — ยังไม่จ่ายเงิน และยังไม่ส่งสลิป',
        'คุณตรวจดูทีละคนได้ก่อนส่งขออนุมัติ',
        'คำนวณใหม่ได้เรื่อยๆ จนกว่าจะล็อกงวด',
      ],
      confirmText: 'คำนวณร่าง',
    });
    if (!ok) return;
    try {
      const res = await runPayroll.mutateAsync({ period, salary_only: salaryOnlyRun || undefined }) as { warnings?: Array<{ emp_code: string; type: string; label: string }> };
      setWarnings(res.warnings ?? []);
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  function openAdjustment(empCode: string) {
    setAdjFor(empCode);
    setAdjDir('add');
    setAdjPreset('bonus');
    setAdjLabel('');
    setAdjAmount('');
  }

  async function submitAdjustment() {
    const amount = Number(adjAmount.replace(/,/g, ''));
    if (!adjFor || !adjLabel.trim() || !Number.isFinite(amount) || amount <= 0) {
      toast.error('กรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    try {
      await upsertAdj.mutateAsync({
        period,
        emp_code: adjFor,
        direction: adjDir,
        label: adjLabel.trim(),
        amount,
        preset: adjDir === 'add' ? adjPreset : undefined,
      });
      toast.success('บันทึกรายการแล้ว');
      setAdjFor(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  // === นำเข้าเงินเพิ่ม/หัก หลายคน (Excel/CSV) ===
  // เทมเพลต = รายชื่อพนักงานล่าสุด + ช่องว่างให้กรอกตัวเลข → กรอกใน Excel → อัปกลับ.
  function downloadAdjustmentTemplate() {
    const header = ['รหัสพนักงาน', 'ชื่อ-สกุล', 'แผนก', 'เงินเพิ่ม(บาท)', 'รายละเอียดเงินเพิ่ม', 'เงินหัก(บาท)', 'รายละเอียดเงินหัก'];
    const body = (employees ?? []).map((e) => [
      e.emp_code,
      `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() + (e.nickname ? ` (${e.nickname})` : ''),
      e.department ?? '',
      '', '', '', '',
    ]);
    const esc = (c: string) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
    const csv = '﻿' + [header, ...body].map((r) => r.map((c) => esc(String(c))).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `เงินเพิ่มหัก-${period}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function handleAdjustmentImport(file: File) {
    try {
      const XLSX = await import('xlsx');
      const wb = file.name.endsWith('.csv')
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
      if (aoa.length < 2) { toast.error('ไฟล์ว่าง — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }

      const head = (aoa[0] ?? []).map((h) => String(h).trim());
      const find = (pred: (h: string) => boolean) => head.findIndex(pred);
      const codeCol = find((h) => h.includes('รหัส') || h.toLowerCase().includes('emp'));
      const addAmtCol = find((h) => h.includes('เงินเพิ่ม') && !h.includes('รายละเอียด'));
      const addLblCol = find((h) => h.includes('รายละเอียดเงินเพิ่ม'));
      const dedAmtCol = find((h) => h.includes('เงินหัก') && !h.includes('รายละเอียด'));
      const dedLblCol = find((h) => h.includes('รายละเอียดเงินหัก'));
      if (codeCol < 0) { toast.error('ไม่พบคอลัมน์ "รหัสพนักงาน" — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }
      if (addAmtCol < 0 && dedAmtCol < 0) { toast.error('ไม่พบคอลัมน์ "เงินเพิ่ม(บาท)" หรือ "เงินหัก(บาท)"'); return; }

      const known = new Set((employees ?? []).map((e) => e.emp_code));
      const entries: { emp_code: string; direction: 'add' | 'deduct'; label: string; amount: number; preset?: 'bonus' }[] = [];
      const unknown: string[] = [];
      const num = (v: unknown) => { const n = Number(String(v ?? '').replace(/[,\s฿]/g, '')); return Number.isFinite(n) ? n : 0; };

      for (let i = 1; i < aoa.length; i++) {
        const row = aoa[i] ?? [];
        const code = String(row[codeCol] ?? '').trim();
        if (!code) continue;
        if (!known.has(code)) { unknown.push(code); continue; }
        const add = addAmtCol >= 0 ? num(row[addAmtCol]) : 0;
        const ded = dedAmtCol >= 0 ? num(row[dedAmtCol]) : 0;
        if (add > 0) entries.push({ emp_code: code, direction: 'add', label: (addLblCol >= 0 ? String(row[addLblCol] ?? '').trim() : '') || 'เงินเพิ่ม', amount: add, preset: 'bonus' });
        if (ded > 0) entries.push({ emp_code: code, direction: 'deduct', label: (dedLblCol >= 0 ? String(row[dedLblCol] ?? '').trim() : '') || 'เงินหัก', amount: ded });
      }

      if (entries.length === 0) {
        toast.error(unknown.length ? `ไม่พบรหัสพนักงานที่ตรงกัน (${unknown.length} แถว) และไม่มียอดให้นำเข้า` : 'ไม่มียอดเงินเพิ่ม/หักในไฟล์ — กรอกตัวเลขก่อนอัปโหลด');
        return;
      }
      const addN = entries.filter((e) => e.direction === 'add').length;
      const dedN = entries.length - addN;
      const ok = await confirm({
        title: 'ยืนยันนำเข้าเงินเพิ่ม/หัก',
        message: `งวด ${period} — เงินเพิ่ม ${addN} รายการ · เงินหัก ${dedN} รายการ${unknown.length ? ` · ข้าม ${unknown.length} แถว (รหัสไม่ตรง)` : ''}\nรายการชื่อเดียวกันของคนเดิมจะถูกทับด้วยยอดใหม่`,
        confirmText: 'นำเข้า',
      });
      if (!ok) return;

      const res = await bulkAdj.mutateAsync({ period, entries });
      await dataEntries.refetch();
      toast.success(`นำเข้าแล้ว ${res.count} รายการ — กด "รันร่างใหม่" เพื่ออัปเดตตัวเลข`);
    } catch (e) {
      toast.error(`นำเข้าไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  // === นำเข้าทั้งงวดจากไฟล์เดียว แบบ "แจงรายการ" (OWNER + HR) ===
  // คอลัมน์: รหัส/ชื่อ/แผนก/เงินเดือน(บาท) + คอลัมน์เพิ่มเติมไม่จำกัด —
  //   ขึ้นต้น "เพิ่ม:<ชื่อ>"  = เงินเพิ่ม 1 บรรทัด (โชว์ชื่อในสลิป)
  //   ขึ้นต้น "หัก:<ชื่อ>"    = เงินหัก 1 บรรทัด
  // เงินเดือน → /owner/salary/bulk · แต่ละคอลัมน์ เพิ่ม/หัก → adjustment bulk (1 บรรทัด/ช่อง).
  // คู่กับโหมด "ไม่คิดภาษี/ปกส.อัตโนมัติ" → สุทธิ = เงินเดือน + เพิ่มทุกช่อง − หักทุกช่อง.
  const PREFIX_ADD = /^\s*เพิ่ม\s*[:：]\s*/;
  const PREFIX_DED = /^\s*หัก\s*[:：]\s*/;

  async function downloadCombinedTemplate() {
    // เทมเพลต .xlsx: คอลัมน์แจงรายการ (เพิ่ม:/หัก: — เพิ่ม/ลบเองได้) + คอลัมน์สรุป
    // ท้ายตาราง (รวมรายได้/รวมรายการหัก/คงเหลือสุทธิ) เป็น "สูตร" คำนวณให้อัตโนมัติ
    // เวลากรอก → เห็นสุทธิทันที ตรวจง่าย. ตอนนำเข้าระบบข้ามคอลัมน์สรุปให้เอง.
    const XLSX = await import('xlsx');
    const earn = ['ค่าคอม', 'เบี้ยขยัน'];               // ตัวอย่างเงินเพิ่ม
    const ded = ['ประกันสังคม', 'ภาษี', 'หัก 3%'];       // ตัวอย่างเงินหัก
    const header = [
      'รหัสพนักงาน', 'ชื่อ-สกุล', 'แผนก', 'เงินเดือน(บาท)',
      ...earn.map((e) => `เพิ่ม:${e}`), ...ded.map((d) => `หัก:${d}`),
      'รวมรายได้', 'รวมรายการหัก', 'คงเหลือสุทธิ',
    ];
    const emps = employees ?? [];
    const body = emps.map((e) => [
      e.emp_code,
      `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() + (e.nickname ? ` (${e.nickname})` : ''),
      e.department ?? '',
      ...Array(1 + earn.length + ded.length).fill(''), // เงินเดือน + เพิ่ม + หัก (เว้นว่างให้กรอก)
      0, 0, 0, // รวมรายได้ / รวมหัก / สุทธิ (จะทับด้วยสูตร)
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    // ตำแหน่งคอลัมน์ (0-based): D=เงินเดือน · เพิ่ม E.. · หัก .. · แล้ว รวมรายได้/รวมหัก/สุทธิ
    const salIdx = 3;
    const earnStart = 4, earnEnd = earnStart + earn.length - 1;
    const dedStart = earnEnd + 1, dedEnd = dedStart + ded.length - 1;
    const incIdx = dedEnd + 1, dedSumIdx = incIdx + 1, netIdx = dedSumIdx + 1;
    const col = (i: number) => XLSX.utils.encode_col(i);
    for (let r = 0; r < emps.length; r++) {
      const R = r + 2; // แถวข้อมูลเริ่มที่ 2 (แถว 1 = หัวตาราง)
      const incF = `${col(salIdx)}${R}${earn.length ? `+SUM(${col(earnStart)}${R}:${col(earnEnd)}${R})` : ''}`;
      const dedF = ded.length ? `SUM(${col(dedStart)}${R}:${col(dedEnd)}${R})` : '0';
      ws[`${col(incIdx)}${R}`] = { t: 'n', f: incF, v: 0 };
      ws[`${col(dedSumIdx)}${R}`] = { t: 'n', f: dedF, v: 0 };
      ws[`${col(netIdx)}${R}`] = { t: 'n', f: `${col(incIdx)}${R}-${col(dedSumIdx)}${R}`, v: 0 };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'เงินเดือน');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    a.download = `เทมเพลตเงินเดือน-${period}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function handleCombinedImport(file: File) {
    // กันเคส: รายชื่อพนักงานยังโหลดไม่เสร็จ (เพิ่งเข้าหน้า/refresh) → known ว่าง →
    // ทุกแถวถูกมองเป็น "รหัสไม่ตรง". แจ้งให้รอแทน error งงๆ.
    if (!employees || employees.length === 0) {
      toast.error('กำลังโหลดรายชื่อพนักงาน รอสักครู่แล้วกดนำเข้าไฟล์อีกครั้ง');
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const wb = file.name.endsWith('.csv')
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
      if (aoa.length < 2) { toast.error('ไฟล์ว่าง — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }

      const head = (aoa[0] ?? []).map((h) => String(h).trim());
      const codeCol = head.findIndex((h) => h.includes('รหัส') || h.toLowerCase().includes('emp'));
      const salCol = head.findIndex((h) => h.includes('เงินเดือน') && !PREFIX_ADD.test(h) && !PREFIX_DED.test(h));
      // คอลัมน์ "กำหนดยอดจ่ายเอง" — มีค่า>0 = ข้ามการคิดอัตโนมัติทั้งหมดให้คนนั้น (ดู pay-override)
      const payOverrideCol = head.findIndex((h) => h.includes('ยอดจ่ายเดือนนี้'));
      // คอลัมน์แจงรายการ (เพิ่ม:/หัก:) — ชื่อ label = ข้อความหลัง ":"
      const itemCols: { col: number; direction: 'add' | 'deduct'; label: string }[] = [];
      head.forEach((h, idx) => {
        if (PREFIX_ADD.test(h)) itemCols.push({ col: idx, direction: 'add', label: h.replace(PREFIX_ADD, '').trim() || 'เงินเพิ่ม' });
        else if (PREFIX_DED.test(h)) itemCols.push({ col: idx, direction: 'deduct', label: h.replace(PREFIX_DED, '').trim() || 'เงินหัก' });
      });
      // รองรับไฟล์เก่า: คอลัมน์เดี่ยว "เงินเพิ่ม(บาท)/เงินหัก(บาท)" + รายละเอียด
      const legacyAddAmt = head.findIndex((h) => h.includes('เงินเพิ่ม') && !h.includes('รายละเอียด'));
      const legacyAddLbl = head.findIndex((h) => h.includes('รายละเอียดเงินเพิ่ม'));
      const legacyDedAmt = head.findIndex((h) => h.includes('เงินหัก') && !h.includes('รายละเอียด'));
      const legacyDedLbl = head.findIndex((h) => h.includes('รายละเอียดเงินหัก'));
      if (codeCol < 0) { toast.error('ไม่พบคอลัมน์ "รหัสพนักงาน" — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }
      if (salCol < 0 && itemCols.length === 0 && legacyAddAmt < 0 && legacyDedAmt < 0 && payOverrideCol < 0) {
        toast.error('ไม่พบคอลัมน์ "เงินเดือน(บาท)" หรือคอลัมน์ "เพิ่ม:.../หัก:..." — ใช้เทมเพลตจากหน้านี้');
        return;
      }

      const known = new Set((employees ?? []).map((e) => e.emp_code));
      const salaryItems: { emp_code: string; base_salary: number }[] = [];
      const entries: { emp_code: string; direction: 'add' | 'deduct'; label: string; amount: number; preset?: 'bonus' }[] = [];
      const payOverrideEntries: { emp_code: string; amount: number }[] = [];
      const unknown: string[] = [];
      const fileCodes = new Set<string>(); // รหัสที่อยู่ในไฟล์ (ตรงกับพนักงานในระบบ)
      const num = (v: unknown) => { const n = Number(String(v ?? '').replace(/[,\s฿]/g, '')); return Number.isFinite(n) ? n : 0; };
      const pushEntry = (code: string, direction: 'add' | 'deduct', label: string, amount: number) => {
        if (amount > 0) entries.push({ emp_code: code, direction, label, amount, ...(direction === 'add' ? { preset: 'bonus' as const } : {}) });
      };

      for (let i = 1; i < aoa.length; i++) {
        const row = aoa[i] ?? [];
        const code = String(row[codeCol] ?? '').trim();
        if (!code) continue;
        if (!known.has(code)) { unknown.push(code); continue; }
        fileCodes.add(code);
        if (salCol >= 0) { const sal = num(row[salCol]); if (sal > 0) salaryItems.push({ emp_code: code, base_salary: sal }); }
        for (const c of itemCols) pushEntry(code, c.direction, c.label, num(row[c.col]));
        if (legacyAddAmt >= 0) pushEntry(code, 'add', (legacyAddLbl >= 0 ? String(row[legacyAddLbl] ?? '').trim() : '') || 'เงินเพิ่ม', num(row[legacyAddAmt]));
        if (legacyDedAmt >= 0) pushEntry(code, 'deduct', (legacyDedLbl >= 0 ? String(row[legacyDedLbl] ?? '').trim() : '') || 'เงินหัก', num(row[legacyDedAmt]));
        if (payOverrideCol >= 0) { const amt = num(row[payOverrideCol]); if (amt > 0) payOverrideEntries.push({ emp_code: code, amount: amt }); }
      }

      if (salaryItems.length === 0 && entries.length === 0 && payOverrideEntries.length === 0) {
        toast.error(unknown.length ? `ไม่พบรหัสพนักงานที่ตรงกัน (${unknown.length} แถว) และไม่มียอดให้นำเข้า` : 'ไม่มีตัวเลขในไฟล์ — กรอกเงินเดือน/เงินเพิ่ม/เงินหักก่อนอัปโหลด');
        return;
      }
      const addN = entries.filter((e) => e.direction === 'add').length;
      const dedN = entries.length - addN;
      const itemNames = itemCols.map((c) => `${c.direction === 'add' ? '+' : '−'}${c.label}`).join(' · ');
      const effDate = salaryEffDate || `${period}-01`;
      // คนในระบบ (active) ที่ไม่อยู่ในไฟล์ → จะถูกพักไม่คิดเงินเดือนงวดนี้
      const notInFile = (employees ?? []).filter((e) => !fileCodes.has(e.emp_code));
      const ok = await confirm({
        title: 'ยืนยันนำเข้าทั้งงวด',
        message: `งวด ${period}\n• ตั้งเงินเดือน ${salaryItems.length} คน (มีผล ${effDate})\n• เงินเพิ่ม ${addN} บรรทัด · เงินหัก ${dedN} บรรทัด${itemNames ? `\n• รายการ: ${itemNames}` : ''}${payOverrideEntries.length ? `\n• กำหนดยอดจ่ายเอง ${payOverrideEntries.length} คน` : ''}${notInFile.length ? `\n• พักคน ${notInFile.length} คนที่ไม่อยู่ในไฟล์ (ไม่คิดเงินเดือนงวดนี้)` : ''}${unknown.length ? `\n• ข้าม ${unknown.length} แถว (รหัสไม่ตรง)` : ''}\nรายการชื่อเดียวกันของคนเดิมจะถูกทับด้วยยอดใหม่`,
        confirmText: 'นำเข้า',
      });
      if (!ok) return;

      let salSet = 0;
      let salFailed = 0;
      if (salaryItems.length > 0) {
        const r = await bulkSalary.mutateAsync({ effective_date: effDate, items: salaryItems });
        salSet = r.set;
        salFailed = r.failed?.length ?? 0;
      }
      let adjN = 0;
      if (entries.length > 0) {
        const res = await bulkAdj.mutateAsync({ period, entries });
        adjN = res.count;
        await dataEntries.refetch();
      }
      let overrideN = 0;
      if (payOverrideEntries.length > 0) {
        const res = await bulkPayOverride.mutateAsync({ period, entries: payOverrideEntries });
        overrideN = res.count;
      }
      // พักคนที่ไม่อยู่ในไฟล์ (ไฟล์ = ตัวกำหนดว่าใครคิดเงินเดือนงวดนี้)
      let excludedN = 0;
      if (fileCodes.size > 0) {
        try { excludedN = (await syncInclusion.mutateAsync({ emp_codes: [...fileCodes] })).excluded; } catch { /* ไม่ critical */ }
      }
      if (salFailed > 0) toast.error(`ตั้งเงินเดือนไม่สำเร็จ ${salFailed} คน (ตรวจรหัส/ยอด)`);
      toast.success(`นำเข้าแล้ว — ตั้งเงินเดือน ${salSet} คน · เงินเพิ่ม/หัก ${adjN} บรรทัด${overrideN ? ` · กำหนดยอดจ่ายเอง ${overrideN} คน` : ''}${excludedN ? ` · พัก ${excludedN} คนที่ไม่อยู่ในไฟล์` : ''}. กด "โหลดข้อมูล" แล้ว "รันร่างใหม่"`);
    } catch (e) {
      toast.error(`นำเข้าไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  async function handlePreview() {
    if (!isOwner) {
      if (!(await confirm({ title: 'ส่งขออนุมัติเงินเดือน', message: `งวด ${period} — ระบบจะแจ้งเจ้าของทาง LINE ให้อนุมัติ`, confirmText: 'ส่งขออนุมัติ' }))) return;
    }
    try {
      const result = await previewPayroll.mutateAsync({ period });
      setPreview(result);
      await loadRows();
      if (!isOwner) toast.success('ส่งขออนุมัติให้เจ้าของแล้ว');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handleDownloadBankFile() {
    const jwt = useAuthStore.getState().jwt;
    const apiBase = import.meta.env.VITE_API_BASE ?? '';
    try {
      const res = await fetch(`${apiBase}/owner/payroll/bank-transfer?period=${encodeURIComponent(period)}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank-transfer-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  function handlePrint() {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast.error('เบราว์เซอร์บล็อกการเปิดหน้าต่าง — อนุญาต popup แล้วลองใหม่'); return; }
    const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
    const today = new Date().toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' });
    const body = rows.map((r, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td>${esc(nameOf(r.emp_code))}<div class="muted">${esc(r.emp_code)}</div></td>
      <td>${r.bank ? esc(r.bank) + (r.bank_account_masked ? ' ••' + esc(r.bank_account_masked) : '') : '-'}</td>
      <td class="r">${fmt(r.gross)}</td>
      <td class="r">${fmt(r.tax_amount)}</td>
      <td class="r">${fmt(r.sso_amount)}</td>
      <td class="r">${fmt(r.deductions_total)}</td>
      <td class="r b">${fmt(r.net)}</td>
    </tr>`).join('');
    const html = `<!doctype html><html lang="th"><head><meta charset="utf-8">
    <title>ใบขออนุมัติเงินเดือน ${esc(period)}</title>
    <style>
      *{font-family:'Sarabun','Tahoma',sans-serif;box-sizing:border-box}
      body{margin:24px;color:#2a2226;font-size:13px}
      h1{font-size:18px;margin:0 0 2px}
      .meta{color:#6b6663;font-size:12px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse}
      th,td{padding:6px 8px;border-bottom:1px solid #e8dde1;text-align:left;vertical-align:top}
      th{background:#fbeaf1;font-weight:600;font-size:12px}
      .r{text-align:right}.c{text-align:center}.b{font-weight:700}
      .muted{color:#9a938c;font-size:10px}
      tfoot td{border-top:2px solid #9a938c;font-weight:700;background:#F9F5F1}
      .sign{display:flex;gap:60px;margin-top:48px}
      .sign div{flex:1;text-align:center;border-top:1px dotted #9a938c;padding-top:6px;color:#6b6663}
      @media print{body{margin:0}}
    </style></head><body>
    <h1>ใบขออนุมัติจ่ายเงินเดือน</h1>
    <div class="meta">งวด ${esc(period)} · พนักงาน ${rows.length} คน · พิมพ์เมื่อ ${esc(today)}</div>
    <table>
      <thead><tr>
        <th class="c">#</th><th>พนักงาน</th><th>ธนาคาร / บัญชี</th>
        <th class="r">รายได้รวม</th><th class="r">ภาษี</th><th class="r">ปกส.</th>
        <th class="r">หักรวม</th><th class="r">โอนสุทธิ</th>
      </tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <td></td><td>รวม ${rows.length} คน</td><td></td>
        <td class="r">${fmt(sum(rows, 'gross'))}</td>
        <td class="r">${fmt(sum(rows, 'tax_amount'))}</td>
        <td class="r">${fmt(sum(rows, 'sso_amount'))}</td>
        <td class="r">${fmt(sum(rows, 'deductions_total'))}</td>
        <td class="r">${fmt(sum(rows, 'net'))}</td>
      </tr></tfoot>
    </table>
    <div class="sign"><div>ผู้จัดทำ (ฝ่ายบุคคล)</div><div>ผู้อนุมัติ (เจ้าของ)</div></div>
    <script>window.onload=function(){window.print()}</script>
    </body></html>`;
    w.document.write(html);
    w.document.close();
  }

  async function handleApprove() {
    const netTotal = sum(rows, 'net');
    const ok = await consequence({
      title: `อนุมัติเงินเดือน งวด ${period}`,
      intro: 'อนุมัติแล้วถือว่าเจ้าของยืนยันยอดจ่ายนี้ — ขั้นต่อไปคือล็อกงวดและจ่ายจริง',
      lines: [
        { label: 'พนักงานในงวดนี้', value: `${rows.length} คน` },
        { label: 'ยอดโอนสุทธิรวม', value: `฿${netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, tone: 'money', emphasis: true },
      ],
      confirmText: 'อนุมัติ',
    });
    if (!ok) return;
    try {
      const r = await approvePayroll.mutateAsync({ period });
      toast.success(`อนุมัติแล้ว — โหมด ${r.mode}`);
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function submitReject() {
    if (!rejectReason.trim()) { toast.error('ระบุเหตุผลที่ปฏิเสธ'); return; }
    try {
      await rejectPayroll.mutateAsync({ period, reason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handleLock() {
    const netTotal = sum(rows, 'net');
    const ok = await consequence({
      title: `ล็อกงวดเงินเดือน ${period}`,
      intro: 'ล็อกแล้วยอดเงินเดือนงวดนี้จะแก้ไขไม่ได้อีก — ใช้เป็นยอดจ่ายจริง',
      lines: [
        { label: 'พนักงานในงวดนี้', value: `${rows.length} คน` },
        { label: 'ยอดโอนสุทธิรวม', value: `฿${netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, tone: 'money', emphasis: true },
      ],
      effects: ['หลังล็อกจะส่งสลิปและออกไฟล์โอนธนาคารได้'],
      danger: true,
      confirmText: 'ล็อกงวด',
    });
    if (!ok) return;
    try {
      const res = await lockPayroll.mutateAsync({ period });
      // #hr-delegation Phase 3 — HR ที่ระดับ pending: คำขอถูกส่งให้เจ้าของ (ยังไม่ปิดงวด)
      if (res && (res as { queued?: boolean }).queued) {
        toast.info((res as { message?: string }).message ?? 'ส่งคำขอปิดงวดให้เจ้าของอนุมัติแล้ว');
        return;
      }
      await loadRows();
      toast.success('ปิดงวดแล้ว');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handleSend() {
    const ok = await consequence({
      title: `ส่งสลิปงวด ${period}`,
      intro: 'ระบบจะส่งสลิปเงินเดือนให้พนักงานทาง LINE',
      lines: [{ label: 'จำนวนสลิปที่จะส่ง', value: `${rows.length} ใบ` }],
      effects: ['พนักงานจะได้รับแจ้งเตือนทันที', 'ส่งซ้ำได้ภายหลังถ้ามีคนตกหล่น'],
      confirmText: 'ส่งสลิป',
    });
    if (!ok) return;
    try {
      await sendSlips.mutateAsync({ period });
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <>
      {/* Stepper ในหน้า — ขั้นตอนทำเงินเดือนงวดนี้ (สีทึบ gradient) */}
      <div className="rounded-2xl p-4 mb-4 bg-gradient-to-r from-[#eaf7fd] via-[#cbebfa] to-[#e6f2cf]">
        <div className="flex items-center gap-1 overflow-x-auto">
          {['รวบรวมเวลา', 'เงินเพิ่ม-หัก', 'ตรวจยอด', 'ปิดงวด + สลิป'].map((label, i, arr) => {
            const done = i < payrollStepIdx;
            const active = i === payrollStepIdx;
            return (
              <div key={label} className="flex items-center gap-2 shrink-0">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${active ? 'bg-[#27AAE1] text-white' : done ? 'bg-[#1c93c8] text-white' : 'bg-white/70 text-warm-gray'}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </span>
                <div className="mr-1">
                  <div className="text-sm font-medium text-ink whitespace-nowrap">{label}</div>
                  <div className="text-[11px] text-warm-gray">{done ? 'เสร็จแล้ว' : active ? 'กำลังทำ' : 'ถัดไป'}</div>
                </div>
                {i < arr.length - 1 && <div className="w-8 h-0.5 bg-white/50 shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>
      <div className="card p-4 mb-4 space-y-3">
        <div>
          <label className="label">เลือกงวด</label>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setRows([]); setPreview(null); setSalaryEffDate(''); }}
            className="input"
          >
            {getLast6Periods().map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <button onClick={loadRows} disabled={loading} className="btn-secondary w-full">
          {loading ? 'กำลังโหลด...' : 'โหลดข้อมูล'}
        </button>
      </div>

      {/* รายการที่ย้ายไปหักรอบจ่ายค่าคอม (คนมีค่าคอม) — เตือนให้ HR เอาไปบันทึกในรอบคอมเอง */}
      {(commAdj.data?.items.length ?? 0) > 0 && (
        <div className="card p-3 mb-4 bg-pink-50 border-pink-200">
          <div className="font-semibold text-sm mb-1">รายการที่ย้ายไปหักรอบจ่ายค่าคอม · งวด {period}</div>
          <div className="text-xs text-gray-600 mb-2">
            คนมีค่าคอม {commAdj.data!.totals.count} คน — งวดเงินเดือนจ่ายแค่ฐาน + ภาษี/ปกส. · รายการด้านล่างต้องเอาไปบันทึกใน
            <span className="font-medium"> รอบจ่ายค่าคอมมิชชั่น</span> เอง (ระบบไม่หักให้อัตโนมัติ)
          </div>
          <div className="space-y-1.5">
            {commAdj.data!.items.map((it) => (
              <div key={it.emp_code} className="text-xs bg-white rounded p-2 border border-pink-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{empName.get(it.emp_code) ?? it.emp_code}</span>
                  <span className={it.net_deferred < 0 ? 'text-rose-600 font-semibold' : 'text-emerald-700 font-semibold'}>
                    สุทธิย้าย ฿{it.net_deferred.toLocaleString('th-TH')}
                  </span>
                </div>
                {(it.earnings.length > 0 || it.deductions.length > 0) && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    {it.earnings.map((e) => `+${e.label} ฿${e.amount.toLocaleString('th-TH')}`).join(' · ')}
                    {it.earnings.length > 0 && it.deductions.length > 0 ? ' · ' : ''}
                    {it.deductions.map((d) => `−${d.label} ฿${d.amount.toLocaleString('th-TH')}`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* นำเข้าทั้งงวดจากไฟล์เดียว (OWNER + HR) — ตั้งเงินเดือน + เงินเพิ่ม/หัก ก่อนรันร่าง */}
      {(isOwner || role === 'HR') && (
        <div className="card p-3 mb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-semibold text-sm">นำเข้าเงินเดือน + เงินเพิ่ม/หัก (ไฟล์เดียว)</div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => void downloadCombinedTemplate()} className="btn-secondary btn-slim inline-flex items-center gap-1 text-xs">
                <FileSpreadsheet className="w-3.5 h-3.5" /> เทมเพลต
              </button>
              <label className="btn-secondary btn-slim inline-flex items-center gap-1 text-xs cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> นำเข้าไฟล์
                <input
                  type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleCombinedImport(f); e.target.value = ''; }}
                />
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-[11px] text-gray-600 shrink-0">วันที่มีผล (เงินเดือน)</label>
            {/* ครอบด้วย div กว้างคงที่ — .input[type=date] ถูก CSS บังคับ width:100%
                (specificity สูงกว่า utility) จึงจำกัดความกว้างที่ wrapper แทน */}
            <div className="w-40 shrink-0">
              <input
                type="date"
                value={salaryEffDate || `${period}-01`}
                onChange={(e) => setSalaryEffDate(e.target.value)}
                className="input text-xs py-1"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">
            โหลด <b>เทมเพลต</b> → กรอก <b>เงินเดือน</b> + คอลัมน์ <b>“เพิ่ม:…”</b> (เงินเพิ่ม) / <b>“หัก:…”</b> (เงินหัก) แจงเป็นรายการ เช่น <b>หัก:ประกันสังคม</b>, <b>หัก:ภาษี</b>, <b>เพิ่ม:ค่าคอม</b> — เพิ่ม/ลบคอลัมน์เองได้ → <b>นำเข้าไฟล์</b>.
            เว้นช่องเงินเดือนว่าง = ไม่แก้ของเดิม (มีผลตาม “วันที่มีผล” ด้านบน). แต่ละคอลัมน์ = 1 บรรทัดในสลิป. เสร็จแล้วกด <b>โหลดข้อมูล</b> แล้ว <b>รันร่างใหม่</b>.
            {' '}<b>ตั้งค่า → “ไม่คิดภาษี/ประกันสังคมอัตโนมัติ”</b> ถ้าจะคุมยอดเองทั้งหมด.
          </p>
        </div>
      )}

      {/* Stage indicator */}
      {status && (
        <div className="card p-3 mb-4">
          <div className="text-xs text-gray-500 mb-2">สถานะงวดนี้</div>
          <div className="flex items-center gap-1 overflow-x-auto">
            {STAGE_ORDER.map((s, i) => {
              const reached = i <= reachedStageIndex(status);
              return (
                <div key={s} className="flex items-center gap-1 shrink-0">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                      reached ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className={`text-xs ${reached ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {STATUS_LABEL[s]}
                  </span>
                  {i < STAGE_ORDER.length - 1 && <div className="w-3 h-0.5 bg-gray-200" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Smart warnings from Run Draft — เตือนเชิงรุก (Non-HR UX §4) */}
      {warnings.length > 0 && (
        <div className="mb-4">
          <ComplianceAlertList
            items={warnings.slice(0, 10).map<ComplianceItem>((w) => {
              if (w.type === 'no_salary_base') {
                return {
                  level: 'block',
                  title: `${nameOf(w.emp_code)} — ยังไม่ตั้งฐานเงินเดือน`,
                  detail: 'พนักงานคนนี้ไม่ถูกคำนวณในงวดนี้ — ตั้งฐานเงินเดือนก่อนแล้วทำเงินเดือนใหม่',
                };
              }
              return {
                level: 'warn',
                title: `${nameOf(w.emp_code)} — ${w.type === 'missing_allowance' ? 'ขาดเบี้ยเลี้ยง' : 'ขาดรายการปรับ'}`,
                detail: `งวดก่อนมีรายการ "${w.label}" แต่งวดนี้ไม่มี — ตรวจว่าตั้งใจตัดออกหรือลืม`,
              };
            })}
            summary={
              <p className="text-sm font-medium text-gray-700">
                มี {warnings.length} เรื่องต้องตรวจก่อนส่งขออนุมัติ
              </p>
            }
          />
          {warnings.length > 10 && (
            <p className="text-xs text-gray-400 mt-1">… และอีก {warnings.length - 10} รายการ</p>
          )}
        </div>
      )}

      {/* Approval-sheet header — the "ใบขออนุมัติ" summary the owner signs off */}
      {rows.length > 0 && status === 'previewed' && (() => {
        const netTotal = sum(rows, 'net');
        const prevNet = preview?.prev_totals.net_sum ?? 0;
        const deltaPct = prevNet > 0 ? ((netTotal - prevNet) / prevNet) * 100 : null;
        return (
          <div className="card p-4 mb-4 bg-gradient-to-br from-brand-50 to-white border-brand-100">
            <div className="text-xs text-brand-700 font-medium">ยอดโอนสุทธิรวมงวดนี้</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="text-2xl font-bold text-brand-900 font-display tabular-nums">
                ฿{netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-gray-500">· พนักงาน {rows.length} คน</div>
              {deltaPct !== null && (
                <div className={`text-sm font-medium ${Math.abs(deltaPct) >= 20 ? 'text-amber-700' : 'text-gray-500'}`}>
                  {deltaPct > 0 ? '▲ +' : '▼ '}{deltaPct.toFixed(1)}% เทียบงวดก่อน
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
              <Chip label="รายได้รวม" value={sum(rows, 'gross')} />
              <Chip label="ภาษีรวม" value={sum(rows, 'tax_amount')} />
              <Chip label="ประกันสังคมรวม" value={sum(rows, 'sso_amount')} />
              <Chip label="โอนสุทธิรวม" value={netTotal} accent />
            </div>
          </div>
        );
      })()}

      {/* Data-entry pane (visible from draft → previewed) */}
      {rows.length > 0 && (status === 'draft' || status === 'previewed') && (
        <div className="card p-3 mb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-semibold text-sm">รายการปรับเพิ่ม / หัก งวดนี้</div>
            <div className="flex items-center gap-1.5">
              <button onClick={downloadAdjustmentTemplate} className="btn-secondary btn-slim inline-flex items-center gap-1 text-xs">
                <FileSpreadsheet className="w-3.5 h-3.5" /> เทมเพลต
              </button>
              <label className="btn-secondary btn-slim inline-flex items-center gap-1 text-xs cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> นำเข้าหลายคน
                <input
                  type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleAdjustmentImport(f); e.target.value = ''; }}
                />
              </label>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mb-2">ใส่หลายคนพร้อมกัน: โหลด <b>เทมเพลต</b> (รายชื่อพนักงานล่าสุด) → กรอกช่องเงินเพิ่ม/หักใน Excel → <b>นำเข้าหลายคน</b> → กดรันร่างใหม่</p>
          {dataEntries.data && dataEntries.data.length > 0 ? (
            <div className="space-y-1 mb-2">
              {dataEntries.data.map((e) => (
                <div key={e.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                  <div className="min-w-0 flex-1">
                    <span>{nameOf(e.emp_code)}</span>
                    <span className="mx-1 text-gray-400">·</span>
                    {e.entry_type === 'variable_allowance' ? (
                      <span>เบี้ยเลี้ยงผันแปร · จำนวน {e.qty}</span>
                    ) : (
                      <span className={e.entry_type === 'adjustment_add' ? 'text-green-700' : 'text-red-700'}>
                        {e.entry_type === 'adjustment_add' ? '+' : '−'}
                        {e.amount.toLocaleString('th-TH')} ({e.adjustment_label})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      if (await confirm({ title: 'ลบรายการนี้?', danger: true, confirmText: 'ลบ' })) {
                        await removeEntry.mutateAsync(e.id);
                      }
                    }}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic mb-2">ยังไม่มีรายการ — รันร่างแล้วดูตัวอย่างได้เลย</p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
            {rows.map((r) => (
              <button
                key={r.emp_code}
                onClick={() => openAdjustment(r.emp_code)}
                className="btn-secondary text-xs justify-start truncate"
              >
                <Plus className="w-3 h-3" />{nameOf(r.emp_code)}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            แก้รายการแล้วต้องรันร่างใหม่ ตัวเลขถึงจะอัปเดต
          </p>
        </div>
      )}

      {/* Filter แผนก/ตำแหน่ง/สาขา */}
      {rows.length > 0 && (orgDepts.length > 0 || orgPositions.length > 0 || branches.length > 0) && (
        <div className="grid grid-cols-3 gap-2 mb-3 max-w-2xl">
          <select className="input" value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPositionFilter(''); }}>
            <option value="">ทุกแผนก</option>
            {orgDepts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          <select className="input" value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
            <option value="">ทุกตำแหน่ง</option>
            {orgPositions.filter((p) => { if (!deptFilter) return true; const did = orgDepts.find((d) => d.name === deptFilter)?.id ?? null; return p.department_id === did; }).map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
          </select>
          <select className="input" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
            <option value="">ทุกสาขา</option>
            {branches.filter((b) => b.is_active).map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
      )}

      {/* Rows table */}
      {rows.length > 0 && (
        <div className="card overflow-hidden mb-4">
          <div className="px-3 py-2 text-[11px] text-gray-500 bg-gray-50 border-b">
            แตะที่พนักงานเพื่อดูรายละเอียด / แก้ไขรายการ
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">พนักงาน</th>
                  <th className="text-left px-3 py-2 font-medium">ธนาคาร / บัญชี</th>
                  <th className="text-right px-3 py-2 font-medium">รายได้รวม</th>
                  <th className="text-right px-3 py-2 font-medium">ภาษี</th>
                  <th className="text-right px-3 py-2 font-medium">ปกส.</th>
                  <th className="text-right px-3 py-2 font-medium">หักรวม</th>
                  <th className="text-right px-3 py-2 font-medium">โอนสุทธิ</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setDetailFor(r.emp_code)}
                    className="cursor-pointer hover:bg-brand-50/60 transition-colors"
                  >
                    <td className="px-3 py-2">
                      <div className="text-sm">{empName.get(r.emp_code) ?? r.emp_code}</div>
                      <div className="text-[11px] text-gray-400 font-mono">{r.emp_code}</div>
                      {overrideAmountByEmp.has(r.emp_code) && (
                        <span className="inline-block mt-0.5 bg-pink-100 text-pink-700 text-[10px] font-medium rounded-full px-1.5 py-0.5">
                          กำหนดยอด ฿{overrideAmountByEmp.get(r.emp_code)!.toLocaleString('th-TH')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {r.bank ? (
                        <span>{r.bank}{r.bank_account_masked ? ` ••${r.bank_account_masked}` : ''}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{r.gross.toLocaleString('th-TH')}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.tax_amount.toLocaleString('th-TH')}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.sso_amount.toLocaleString('th-TH')}</td>
                    <td className="px-3 py-2 text-right text-red-700">{r.deductions_total.toLocaleString('th-TH')}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.net === 0 ? 'text-amber-700' : ''}`}>
                      {r.net.toLocaleString('th-TH')}
                      {r.net === 0 && <AlertTriangle className="inline w-3 h-3 ml-1 -mt-0.5" />}
                    </td>
                    <td className="pr-2 text-gray-300"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <tr>
                  <td className="px-3 py-2">รวม {filteredRows.length} คน</td>
                  <td className="px-3 py-2 text-gray-300">—</td>
                  <td className="px-3 py-2 text-right">{sum(filteredRows, 'gross').toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right">{sum(filteredRows, 'tax_amount').toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right">{sum(filteredRows, 'sso_amount').toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right text-red-700">{sum(filteredRows, 'deductions_total').toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right text-brand-900">{sum(filteredRows, 'net').toLocaleString('th-TH')}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Preview details */}
      {preview && (
        <div className="card p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">เปรียบเทียบกับงวด {preview.prev_period}</div>
            <div className="text-xs text-gray-500">
              ผิดปกติ {preview.employees.filter((e) => e.anomalies.length).length} รายการ
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs mb-2">
            <Total label="รวมรายได้" cur={preview.totals.gross_sum} prev={preview.prev_totals.gross_sum} />
            <Total label="รวมหัก"   cur={preview.totals.deductions_sum} prev={preview.prev_totals.deductions_sum} />
            <Total label="รวมสุทธิ"  cur={preview.totals.net_sum} prev={preview.prev_totals.net_sum} />
          </div>
          {preview.employees.filter((e) => e.anomalies.length).length > 0 && (
            <div className="bg-amber-50 rounded p-2 mt-2 text-xs space-y-1">
              <div className="flex items-center gap-1 font-medium text-amber-800">
                <AlertTriangle className="w-3 h-3" /> รายการที่ผิดปกติ
              </div>
              {preview.employees
                .filter((e) => e.anomalies.length)
                .map((e) => (
                  <div key={e.emp_code} className="text-amber-900">
                    <span>{nameOf(e.emp_code)}</span> · สุทธิ {e.net.toLocaleString('th-TH')}
                    {e.delta_pct !== null && (
                      <span className="ml-1">
                        ({e.delta_pct > 0 ? '+' : ''}{e.delta_pct.toFixed(1)}% เทียบงวดก่อน)
                      </span>
                    )}
                    {e.anomalies.includes('net_zero') && <span className="ml-1 text-red-700">[สุทธิ=0]</span>}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Print/send the approval request — HR tool (owner sees it only in solo) */}
      {canPrintRequest && rows.length > 0 && status && status !== 'voided' && (
        <button onClick={handlePrint} className="btn-secondary w-full mb-2">
          <Printer className="w-4 h-4" /> พิมพ์ใบขออนุมัติ
        </button>
      )}

      {/* Bank-transfer file — HR+ (after approval; real account numbers) */}
      {rows.length > 0 && ['approved', 'locked', 'pdf_ready', 'sending', 'partial_sent', 'sent'].includes(status ?? '') && (
        <button onClick={handleDownloadBankFile} className="btn-secondary w-full mb-2">
          <Banknote className="w-4 h-4" /> ดาวน์โหลดไฟล์โอนธนาคาร (CSV)
        </button>
      )}

      {/* Stage actions */}
      <div className="space-y-2">
        {(status === null || status === 'draft') && (
          <>
            <label className="flex items-start gap-2.5 px-1 py-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={salaryOnlyRun}
                onChange={(e) => setSalaryOnlyRun(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-600"
              />
              <span className="text-sm text-gray-600">
                ข้ามการลงเวลา/ลา/OT งวดนี้ — จ่ายเต็มตามฐานเงินเดือน
                <span className="block text-xs text-gray-400">คิดเฉพาะ เงินเดือน เงินเพิ่ม รายการหัก และภาษี/ประกันสังคม</span>
              </span>
            </label>
            <button
              onClick={handleRun}
              disabled={runPayroll.isPending}
              className="btn-primary w-full"
            >
              <Play className="w-4 h-4" /> {status === 'draft' ? 'รันร่างใหม่' : 'รันร่าง'}
            </button>
          </>
        )}

        {status === 'draft' && (
          <button
            onClick={handlePreview}
            disabled={previewPayroll.isPending}
            className="btn-cta w-full"
          >
            <Eye className="w-4 h-4" /> {isOwner ? 'ดูตัวอย่าง + เทียบงวดก่อน' : 'ส่งขออนุมัติ'}
          </button>
        )}

        {status === 'previewed' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleApprove}
              disabled={approvePayroll.isPending}
              className="btn-cta"
            >
              <CheckCircle2 className="w-4 h-4" /> อนุมัติ
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={rejectPayroll.isPending}
              className="btn-secondary text-red-700 border-red-200"
            >
              <XCircle className="w-4 h-4" /> ปฏิเสธ
            </button>
          </div>
        )}

        {status === 'approved' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleLock}
              disabled={lockPayroll.isPending}
              className="btn-primary"
            >
              <Lock className="w-4 h-4" /> ล็อกงวด
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={rejectPayroll.isPending}
              className="btn-secondary text-amber-700 border-amber-200"
            >
              <RotateCw className="w-4 h-4" /> ถอยกลับเป็นร่าง
            </button>
          </div>
        )}

        {(status === 'locked' || status === 'pdf_ready' || status === 'partial_sent') && (
          <button
            onClick={handleSend}
            disabled={sendSlips.isPending}
            className="btn-cta w-full"
          >
            <Send className="w-4 h-4" /> ส่งสลิป
          </button>
        )}

        {status === 'sent' && (
          <div className="text-center text-sm text-green-700 py-2">
            ✓ ส่งสลิปครบทุกคนแล้ว
          </div>
        )}
      </div>

      {/* Deliveries breakdown — shows after lock onwards */}
      {showDeliveries && deliveries.data && deliveries.data.length > 0 && (
        <div className="card p-3 mt-4">
          <div className="font-semibold mb-2">การส่งสลิปรายคน</div>
          <div className="space-y-1">
            {deliveries.data.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs border-b border-gray-50 last:border-0 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <DeliveryIcon s={d.push_status} />
                  <span className="font-mono">{d.emp_code}</span>
                  {d.push_attempts > 0 && (
                    <span className="text-gray-400">·{d.push_attempts} ครั้ง</span>
                  )}
                  {d.push_last_error && (
                    <span className="text-red-600 truncate" title={d.push_last_error}>
                      {d.push_last_error}
                    </span>
                  )}
                </div>
                {(d.push_status === 'failed' || d.push_status === 'blocked') && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await retryDelivery.mutateAsync(d.id);
                        if (r.error) toast.error(`ยังส่งไม่สำเร็จ: ${r.error}`);
                        else toast.success('ส่งซ้ำสำเร็จ');
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
                      }
                    }}
                    disabled={retryDelivery.isPending}
                    className="btn-secondary text-xs py-1 px-2"
                  >
                    <RotateCw className="w-3 h-3" /> retry
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-employee detail drawer — breakdown + this person's adjustments */}
      <PersonDetailModal
        period={period}
        empCode={detailFor}
        name={detailFor ? nameOf(detailFor) : ''}
        editable={status === 'draft' || status === 'previewed'}
        entries={(dataEntries.data ?? []).filter((e) => e.emp_code === detailFor)}
        overrideAmount={detailFor ? overrideAmountByEmp.get(detailFor) ?? null : null}
        onSetOverride={async (amount) => {
          if (!detailFor) return;
          await setPayOverride.mutateAsync({ period, emp_code: detailFor, amount, source: 'manual' });
        }}
        onClose={() => setDetailFor(null)}
        onAdd={() => detailFor && openAdjustment(detailFor)}
        onDelete={async (id) => {
          if (await confirm({ title: 'ลบรายการนี้?', danger: true, confirmText: 'ลบ' })) {
            await removeEntry.mutateAsync(id);
          }
        }}
      />

      {/* Adjustment modal — replaces the old 4× window.prompt flow */}
      <Modal open={adjFor !== null} onClose={() => setAdjFor(null)} title={`เพิ่มรายการปรับ — ${adjFor ? nameOf(adjFor) : ''}`}>
        <div className="space-y-4">
          <div>
            <label className="label">ทิศทาง</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setAdjDir('add')} className={`w-full ${adjDir === 'add' ? 'btn-cta' : 'btn-secondary'}`}>
                <Plus className="w-4 h-4" /> เงินเพิ่ม
              </button>
              <button type="button" onClick={() => setAdjDir('deduct')} className={`w-full ${adjDir === 'deduct' ? 'btn-danger' : 'btn-secondary'}`}>
                <Minus className="w-4 h-4" /> หักเงิน
              </button>
            </div>
          </div>
          {adjDir === 'add' && (
            <div>
              <label className="label">ประเภท</label>
              <div className="space-y-1.5 text-sm">
                {([
                  ['bonus', 'โบนัส / เงินเพิ่มพิเศษ', 'เสียภาษี · ไม่เข้าประกันสังคม'],
                  ['wage_correction', 'ปรับค่าจ้างตกหล่น', 'เสียภาษี + ปกส. + PF'],
                  ['reimbursement', 'เบิกคืน / ไม่ใช่เงินได้', 'ไม่เสียภาษี'],
                ] as const).map(([val, label, sub]) => (
                  <label key={val} className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${adjPreset === val ? 'border-brand-500 bg-brand-50' : 'border-gray-200'}`}>
                    <input type="radio" name="adj-preset" checked={adjPreset === val} onChange={() => setAdjPreset(val)} className="mt-0.5 accent-brand-600" />
                    <span><b>{label}</b><br /><span className="text-xs text-gray-500">{sub}</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">ชื่อรายการ</label>
              <input className="input" value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} placeholder="เช่น โบนัสพิเศษ Q1" />
            </div>
            <div>
              <label className="label">จำนวน (บาท)</label>
              <input className="input" inputMode="numeric" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setAdjFor(null)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitAdjustment} disabled={upsertAdj.isPending}>บันทึกรายการ</button>
          </div>
        </div>
      </Modal>

      {/* Reject / revert reason modal — same backend action, status-aware copy */}
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title={status === 'approved' ? 'ถอยกลับเป็นร่าง' : 'ปฏิเสธเงินเดือน'}>
        <div className="space-y-3">
          {status === 'approved' && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
              งวดนี้อนุมัติแล้วแต่ยังไม่ได้ล็อก — ถอยกลับเป็นร่างเพื่อแก้ไขแล้วรันร่าง/ขออนุมัติใหม่ได้
            </p>
          )}
          <div>
            <label className="label">{status === 'approved' ? 'เหตุผลที่ถอยกลับ' : 'เหตุผลที่ปฏิเสธ'}</label>
            <textarea className="input" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="ระบุเหตุผล…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setRejectOpen(false)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitReject} disabled={rejectPayroll.isPending}>
              {status === 'approved' ? 'ถอยกลับเป็นร่าง' : 'ปฏิเสธ'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page kept for legacy /owner/payroll (now redirects via hub). */
export function OwnerPayroll() {
  return (
    <AdminLayout title="ทำเงินเดือน">
      <OwnerPayrollContent />
    </AdminLayout>
  );
}

/**
 * Per-employee breakdown drawer. Reads the itemized earnings/deductions
 * snapshot from the last Run Draft (calculation_log.line_items) so the owner
 * sees how the number was reached, plus this period's adjustments with
 * inline add/delete. Edits only take effect after pressing "รันร่างใหม่".
 */
function PersonDetailModal({
  period, empCode, name, editable, entries, overrideAmount, onSetOverride, onClose, onAdd, onDelete,
}: {
  period: string;
  empCode: string | null;
  name: string;
  editable: boolean;
  entries: DataEntryRow[];
  overrideAmount: number | null;
  onSetOverride: (amount: number) => Promise<void>;
  onClose: () => void;
  onAdd: () => void;
  onDelete: (id: string) => Promise<void>;
}) {
  const detail = usePayrollRunDetail(period, empCode);
  const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const d = detail.data;
  const toast = useToast();
  const confirm = useConfirm();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideInput, setOverrideInput] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  // เปิดคนใหม่ → เคลียร์ฟอร์มกำหนดยอดของคนก่อนหน้า
  useEffect(() => { setOverrideOpen(false); setOverrideInput(''); }, [empCode]);

  async function submitOverride() {
    const amount = Number(overrideInput.replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('กรอกจำนวนเงินให้ถูกต้อง');
      return;
    }
    setOverrideSaving(true);
    try {
      await onSetOverride(amount);
      toast.success('กำหนดยอดจ่ายงวดนี้แล้ว');
      setOverrideOpen(false);
      setOverrideInput('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setOverrideSaving(false);
    }
  }

  async function cancelOverride() {
    if (!(await confirm({
      title: 'ยกเลิกการกำหนดยอด',
      message: 'ยกเลิกยอดที่กำหนดไว้? ระบบจะกลับไปคิดเงินเดือนอัตโนมัติให้คนนี้',
      confirmText: 'ยกเลิกการกำหนดยอด',
    }))) return;
    setOverrideSaving(true);
    try {
      await onSetOverride(0);
      toast.success('ยกเลิกการกำหนดยอดแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setOverrideSaving(false);
    }
  }
  return (
    <Modal open={empCode !== null} onClose={onClose} title={`รายละเอียดเงินเดือน — ${name}`}>
      {detail.isLoading ? (
        <p className="text-sm text-gray-400 py-6 text-center">กำลังโหลด…</p>
      ) : !d ? (
        <p className="text-sm text-gray-400 py-6 text-center">ยังไม่มีข้อมูลคนนี้ในงวดนี้ — กรุณารันร่างก่อน</p>
      ) : (
        <div className="space-y-4">
          {/* Earnings */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายได้</div>
            <div className="space-y-0.5 text-sm">
              {d.earnings.length === 0 && <div className="text-gray-400 italic text-xs">— ไม่มีรายการ</div>}
              {d.earnings.map((e, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-700">{e.label}</span>
                  <span className="text-green-700">{baht(e.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 mt-1 font-medium">
                <span>รวมรายได้</span><span>{baht(d.gross)}</span>
              </div>
            </div>
          </div>
          {/* Deductions */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายการหัก</div>
            <div className="space-y-0.5 text-sm">
              {d.deductions.length === 0 && <div className="text-gray-400 italic text-xs">— ไม่มีรายการ</div>}
              {d.deductions.map((e, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-700">{e.label}</span>
                  {/* #10 — negative = §76 credit (ยกไปงวดหน้า) → show as +, not −− */}
                  <span className={e.amount < 0 ? 'text-green-700' : 'text-red-700'}>{e.amount < 0 ? `+${baht(-e.amount)}` : `−${baht(e.amount)}`}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 mt-1 font-medium">
                {/* #12 — use the line total (incl. หักลา/ขาด + §76 carry/credit lines) so the
                    displayed total matches the itemized rows. deductions_total is net of leave/absent. */}
                <span>รวมหัก</span><span className="text-red-700">−{baht(d.deductions_line_total)}</span>
              </div>
            </div>
          </div>
          {/* Net */}
          <div className="flex justify-between items-center bg-brand-50 rounded-lg px-3 py-2">
            <span className="font-semibold text-brand-900">เงินสุทธิ</span>
            <span className="text-lg font-bold text-brand-900">{baht(d.net)}</span>
          </div>
          {/* YTD */}
          {d.ytd && (
            <div className="grid grid-cols-3 gap-2 text-center">
              {([['รายได้สะสม', d.ytd.gross], ['ภาษีสะสม', d.ytd.tax], ['สุทธิสะสม', d.ytd.net]] as const).map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded p-1.5">
                  <div className="text-[10px] text-gray-500">{label}</div>
                  <div className="text-xs font-semibold">{val != null ? baht(val) : '—'}</div>
                </div>
              ))}
            </div>
          )}
          {/* This period's adjustments */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายการปรับงวดนี้</div>
            {entries.length === 0 ? (
              <p className="text-xs text-gray-400 italic">ยังไม่มีรายการปรับ</p>
            ) : (
              <div className="space-y-1">
                {entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                    <span className="min-w-0 flex-1">
                      {e.entry_type === 'variable_allowance' ? (
                        <span>เบี้ยเลี้ยงผันแปร · จำนวน {e.qty}</span>
                      ) : (
                        <span className={e.entry_type === 'adjustment_add' ? 'text-green-700' : 'text-red-700'}>
                          {e.entry_type === 'adjustment_add' ? '+' : '−'}
                          {e.amount.toLocaleString('th-TH')} ({e.adjustment_label})
                        </span>
                      )}
                    </span>
                    {editable && (
                      <button onClick={() => onDelete(e.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* กำหนดยอดจ่ายงวดนี้เอง — ข้ามการคิดอัตโนมัติทั้งหมด (ขาด/OT/เบี้ยเลี้ยง) ให้คนนี้ */}
          {editable && (
            <div className="border-t border-gray-100 pt-3">
              {overrideAmount != null ? (
                <div className="bg-pink-50 rounded-lg p-2.5 space-y-1.5">
                  <div className="text-sm font-medium text-pink-800">
                    กำหนดยอดจ่ายงวดนี้ ฿{baht(overrideAmount)}
                  </div>
                  <p className="text-[11px] text-gray-500">
                    จ่ายฐานเท่านี้เดือนนี้ — ระบบจะข้ามการคิดอัตโนมัติ (ขาด/OT/เบี้ยเลี้ยง) ให้คนนี้
                  </p>
                  <button onClick={cancelOverride} disabled={overrideSaving} className="btn-secondary w-full text-xs">
                    ยกเลิกการกำหนดยอด
                  </button>
                </div>
              ) : overrideOpen ? (
                <div className="bg-pink-50 rounded-lg p-2.5 space-y-1.5">
                  <label className="label">ยอดจ่ายงวดนี้ (บาท)</label>
                  <input
                    className="input"
                    inputMode="numeric"
                    value={overrideInput}
                    onChange={(e) => setOverrideInput(e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-[11px] text-gray-500">
                    จ่ายฐานเท่านี้เดือนนี้ — ระบบจะข้ามการคิดอัตโนมัติ (ขาด/OT/เบี้ยเลี้ยง) ให้คนนี้
                  </p>
                  <div className="flex gap-2">
                    <button className="btn-secondary flex-1 text-xs" onClick={() => setOverrideOpen(false)}>ยกเลิก</button>
                    <button className="btn-primary flex-1 text-xs" onClick={submitOverride} disabled={overrideSaving}>บันทึก</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setOverrideOpen(true)} className="btn-secondary w-full text-xs">
                  กำหนดยอดจ่ายงวดนี้
                </button>
              )}
            </div>
          )}
          {editable ? (
            <>
              <button onClick={onAdd} className="btn-secondary w-full">
                <Plus className="w-4 h-4" /> เพิ่มรายการปรับ
              </button>
              <p className="text-[11px] text-amber-700 text-center">
                แก้รายการแล้วต้องกด “รันร่างใหม่” ตัวเลขถึงจะอัปเดต
              </p>
            </>
          ) : (
            <p className="text-[11px] text-gray-400 text-center">งวดนี้ปิดแก้ไขแล้ว (เลยขั้นตอนตัวอย่าง)</p>
          )}
        </div>
      )}
    </Modal>
  );
}

function sum(rows: PayrollRow[], key: 'gross' | 'tax_amount' | 'sso_amount' | 'deductions_total' | 'net'): number {
  return rows.reduce((t, r) => t + (r[key] ?? 0), 0);
}

function Chip({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`rounded-lg p-2 ${accent ? 'bg-brand-600 text-white' : 'bg-white border border-gray-100'}`}>
      <div className={`text-[10px] ${accent ? 'text-brand-100' : 'text-gray-500'}`}>{label}</div>
      <div className="text-sm font-semibold">{value.toLocaleString('th-TH')}</div>
    </div>
  );
}

function DeliveryIcon({ s }: { s: 'pending' | 'sent' | 'failed' | 'blocked' }) {
  if (s === 'sent') return <MailCheck className="w-3.5 h-3.5 text-green-700" />;
  if (s === 'failed') return <MailX className="w-3.5 h-3.5 text-red-600" />;
  if (s === 'blocked') return <MailWarning className="w-3.5 h-3.5 text-amber-700" />;
  return <Send className="w-3.5 h-3.5 text-gray-400" />;
}

function Total({ label, cur, prev }: { label: string; cur: number; prev: number }) {
  const delta = prev !== 0 ? ((cur - prev) / prev) * 100 : null;
  const color = delta !== null && Math.abs(delta) >= 20 ? 'text-amber-700' : 'text-gray-600';
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-[10px] text-gray-500">{label}</div>
      <div className="font-semibold">{cur.toLocaleString('th-TH')}</div>
      <div className={`text-[10px] ${color}`}>
        {prev > 0 ? `prev ${prev.toLocaleString('th-TH')}` : ''}
        {delta !== null && (
          <span className="ml-1">({delta > 0 ? '+' : ''}{delta.toFixed(1)}%)</span>
        )}
      </div>
    </div>
  );
}
